import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgProgressbar } from 'ngx-progressbar';
import { StorageModule } from '@ngx-pwa/local-storage';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';
import { SharedModule } from './modules/shared/shared.module';
import { AppRoutes } from './app.routes';
import { ServerErrorInterceptor } from './modules/shared/interceptors/serverErrorInterceptor';
import { AuthModule } from './modules/auth/auth.module';
import { unAuthorisedComponent } from './unauthorised/unauthorised.component';
import { CustomToastrComponent } from './modules/shared/components/common/custom-toastr/custom-toastr.component';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { SignOidcComponent } from './modules/auth/signOidc-component/sign-oidc.component';
import { IonicModule } from '@ionic/angular';
import { WtStorageService } from './modules/shared/services/wt-storage.service';
import { QuicklinkModule } from 'ngx-quicklink';
import { AzureDebugInterceptor } from './modules/shared/interceptors/azure-debug.interceptor';
import { UserAccountSelectComponent } from './modules/account-selection/components/user-accounts/user-accounts.component';
import { provideTranslateService } from '@ngx-translate/core';
import { NoAccessComponent } from './no-access/no-access.component';
import { MapsLoaderService } from './modules/shared/services/mapsLoader.service';
import { CoreModule } from './modules/core/core.module';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  CalendarDateFormatter,
  CalendarModule,
  CalendarMomentDateFormatter,
  DateAdapter,
  MOMENT
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ModalModule } from 'ngx-bootstrap/modal';
import { DragulaModule } from 'ng2-dragula';
import { QuillModule } from 'ngx-quill';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { modalConfigProviders } from './modules/shared/providers/modal-config.providers';
import { NgxEchartsModule } from 'ngx-echarts';
import * as moment_ from 'moment';
const moment = (moment_ as any).default || moment_;
defineLocale('engb', enGbLocale);

export function onAppInit(storage: WtStorageService): () => Promise<any> {
  return storage.loadAllDataInMemoryFromStorage;
}

export function googleMapsLoader(mapsLoaderService: MapsLoaderService) {
  return () => {
    return new Promise<void>((resolve) => {
      mapsLoaderService.loadGoogleMapsAPIs().subscribe({
        next: () => {
          resolve();
        },
        error: (err) => {
          console.error('Google Maps APIs failed to load. Map features will be unavailable.', err);
          resolve();
        },
      });
    });
  };
}

export function momentAdapterFactory() {
  return adapterFactory(moment);
}

const APP_MODULES = [CoreModule.forRoot(), SharedModule];

@NgModule({
  declarations: [
    AppComponent,
    unAuthorisedComponent,
    UserAccountSelectComponent,
    SignOidcComponent,
    AccessDeniedComponent,
    NoAccessComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutes,
    BrowserModule,
    HammerModule,
    QuicklinkModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      toastComponent: CustomToastrComponent,
      autoDismiss: true,
      maxOpened: 1,
    }),
    NgProgressbar,

    CalendarModule.forRoot(
      { provide: DateAdapter, useFactory: momentAdapterFactory },
      { dateFormatter: { provide: CalendarDateFormatter, useClass: CalendarMomentDateFormatter } }
    ),
    BsDatepickerModule.forRoot(),
    BsDropdownModule.forRoot(),
    TimepickerModule.forRoot(),
    TabsModule.forRoot(),
    ModalModule.forRoot(),
    DragulaModule.forRoot(),
    QuillModule.forRoot(),

    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),

    AuthModule.forRoot(),
    ...APP_MODULES,
    StorageModule.forRoot({ IDBNoWrap: true }),

    IonicModule.forRoot({
      scrollAssist: true,
    })
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: onAppInit,
      deps: [WtStorageService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AzureDebugInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: googleMapsLoader,
      multi: true,
      deps: [MapsLoaderService]
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: "./assets/i18n/translations/", suffix: ".json", useHttpBackend: true }),
      fallbackLang: 'en',
      lang: 'en'
    }),

    { provide: MOMENT, useValue: moment },
    FileOpener,
    ...modalConfigProviders
  ]
})
export class AppModule { }
